/**
 * Confirm a connection-lost verdict against MAIN before acting on it.
 *
 * `isConnectionLostError` reads an error STRING, and for MSSQL that string set
 * includes `Connection … is not active. Connect first.` — which main returns
 * when it holds no pool for the id. That covers two states the renderer has no
 * way to tell apart from the text alone:
 *
 *   • the connection was open and DROPPED — a real loss, act on it;
 *   • the connection was NEVER OPENED YET — the query was issued against a
 *     connection a resume had deferred, and the background sweep opens it a
 *     moment later.
 *
 * The second one is not hypothetical and not rare: a resume seeds every
 * deferred connection into `activeConnections` so it stays visible, restores
 * the tabs, and sweeps the connections open right after — so metadata queries
 * and the dial race by construction. A verdict from a query issued BEFORE the
 * connect can land AFTER it, and marking the connection disconnected then is
 * simply wrong: main is holding a live, healthy pool, the app just stopped
 * believing in it. The user sees an amber "Disconnected" that connects
 * instantly when clicked, because there was never anything wrong with it.
 *
 * Main is the authority on whether a pool exists, so ask it. This costs one
 * IPC round-trip on an error path that was already going to change the UI.
 *
 * ⚠️ Asking main is NOT enough on its own, and that was the miss this file
 * originally shipped with. The probe answers about the moment it was ASKED —
 * and it is asked at exactly the moment that produced the error, i.e. while
 * the connection still has no pool. In the resume race the probe therefore
 * agrees with the stale error ("no pool"), the round-trip then takes long
 * enough for the dial to land, and the verdict is applied to a connection that
 * is by then OPEN. The result is the reported bug verbatim: an amber
 * "Disconnected" over a live pool, which reconnects instantly because there
 * was never anything wrong with it.
 *
 * So the verdict is re-validated against the store when it comes back, and
 * dropped if a connect overtook it. The check is deliberately asymmetric — a
 * connection that was ALREADY open when we asked and is still open now is a
 * genuine drop and must still be reported.
 */
import { useConnectionStore } from '../store/connectionStore';
import { checkConnectionStatus } from './sqlConnectionService';

/** What the connection looked like at the moment the verdict was asked for. */
type ConnectionLiveness = {
  status: string | undefined;
  operation: string | undefined;
};

function readLiveness(connectionId: string): ConnectionLiveness {
  const state = useConnectionStore.getState();
  return {
    status: state.activeConnections.find((conn) => conn.id === connectionId)?.status,
    operation: state.connectionOperationById[connectionId],
  };
}

/**
 * Did a connect land — or start — for this connection while we were asking?
 *
 * `before.status !== 'connected'` is what keeps genuine drops reportable: a
 * connection that was open before the probe and is open after it never
 * transitioned, so nothing overtook anything and the drop stands.
 */
function connectOvertookVerdict(connectionId: string, before: ConnectionLiveness): boolean {
  const after = readLiveness(connectionId);
  if (after.operation === 'connecting') return true;
  return before.status !== 'connected' && after.status === 'connected';
}

/**
 * Resolves true when main agrees the connection is down AND nothing opened it
 * while we were asking, i.e. the caller should go ahead and mark it
 * disconnected.
 *
 * Fails CLOSED — an unreachable/erroring status check is still treated as
 * "down", keeping the old behaviour for every case except the ones this exists
 * to catch (main explicitly reporting the connection is up, and a dial landing
 * mid-verdict).
 */
export async function confirmConnectionLost(connectionId: string): Promise<boolean> {
  const before = readLiveness(connectionId);
  // A dial in flight settles this without asking anyone: the error was raised
  // against the state that dial is replacing, so it describes nothing the user
  // needs told. Its outcome — success or failure — is what updates the UI.
  if (before.operation === 'connecting') return false;

  let lost = true;
  try {
    const envelope = await checkConnectionStatus(connectionId);
    lost = envelope.status !== 'connected';
  } catch {
    lost = true;
  }
  if (!lost) return false;

  return !connectOvertookVerdict(connectionId, before);
}
